import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { PickerIdsSchema, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
import { BonusQtyStringSchema, SalesHeaderInputFields, SalesHeaderPatchFields, SalesHeaderResponseFields, TwoWayAmountSchema, CommissionInputFields, CommissionPatchFields, CommissionResponseFields, } from './sales-invoices.js';
const c = initContract();
/**
 * Sales Orders (PART 6, SAL-02) — the list-and-detail screen with the
 * five-stage order-to-cash stepper.
 *
 * Permission area: `sales`, per action:
 *   list/get = sales.view · create draft = sales.create · edit draft =
 *   sales.edit · delete draft = sales.delete · CONFIRM and CANCEL =
 *   sales.approve (16.3: Approve "for confirming", and its X half covers
 *   calling a committed document off) · create-invoice = sales.create
 *   (it creates a new draft document).
 *
 * Lifecycle (SAL-02 §7): draft → confirmed → … → cancelled. The fulfillment
 * statuses partially_delivered / fulfilled are set by delivery-note posting
 * and closed by the payment sync; the wire enum carries all six.
 *
 * The STAGE is derived, never stored — that is BR-SAL-02-02 ("stage and
 * status must be kept in step") made structural. A confirmed delivery note
 * is what unlocks the delivery stage (BR-SAL-02-04).
 */
// ── The fixed sentences of SAL-02 ───────────────────────────────────────────
export const ORDER_MESSAGES = {
    /** ERR-SAL-02-01. */
    noneMatchFilter: 'No orders match this filter.',
    /** ERR-SAL-02-02. */
    selectAnOrder: 'Select an order to see its flow and lines.',
    /** BR-SAL-02-04 / ERR-SAL-02-03 — nothing delivered yet. */
    noDeliveryYet: (code) => `No delivery note has been confirmed for ${code}. Create one first.`,
    /** BR-SAL-02-05 / ERR-SAL-02-04. */
    noInvoiceYet: (code) => `No invoice has been posted for ${code}. Create one first.`,
    /** BR-SAL-02-06 / ERR-SAL-02-05. */
    cancelledNoDocs: (code) => `${code} was cancelled. It cannot be delivered or invoiced.`,
    /** Confirming needs substance — same rules as posting, order-worded. */
    noLines: 'Add at least one line before confirming this order.',
    zeroTotal: 'An order for 0.00 cannot be confirmed.',
    /** The one-way ladder. */
    notADraftConfirm: 'Only a draft order can be confirmed.',
    notDraftDelete: 'Only a draft order can be deleted.',
    confirmedLocked: 'This order is confirmed. Its lines and totals can no longer change.',
    cancelledTerminal: 'This order was cancelled. It can no longer change.',
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'An order can only be cancelled with a reason, which is stored on the order and written to the audit log.',
    /** The nothing-re-bills guard: every quantity is on an invoice already. */
    alreadyInvoiced: (code) => `${code} is fully invoiced. There is nothing left to bill.`,
    /** SAL-02 §7 "invoice what went" — and it all went onto invoices already. */
    nothingNewToBill: (code) => `Everything delivered on ${code} has been invoiced. Deliver more before billing again.`,
    /** Invoicing a promise that was never confirmed is the SAL-02 ladder broken. */
    invoiceNeedsConfirmed: 'Confirm the order before creating its invoice.',
    /**
     * BR-MD-01-10, Block over limit — the spec's own sentence, its two figures
     * grouped with commas (N-2): "This order takes Northwind Traders to
     * 158,900.00, over their 150,000.00 limit."
     */
    overCreditLimit: (name, to, limit) => `This order takes ${name} to ${to}, over their ${limit} limit.`,
    /** BR-MD-01-10, Warn only — "the same sentence plus 'Continue?'". */
    overCreditLimitContinue: (name, to, limit) => `This order takes ${name} to ${to}, over their ${limit} limit. Continue?`,
    /**
     * Wave 4B — AMEND after confirmation. A confirmed order may go back to
     * draft only while nothing stands against it: a delivery note or an
     * invoice (cancelled ones aside) references its line rows, so the refusal
     * names the document that blocks the way back.
     */
    reopenBlocked: (code) => `This order cannot be amended: ${code} already stands against it. Cancel that document first, or cancel the order.`,
    reopenNotConfirmed: 'Only a confirmed order can be amended.',
    reopenReasonRequired: 'An order can only be reopened with a reason, which is written to the audit log.',
};
/**
 * D46 — the sales-order family's structured refusals (the d46po wave): key +
 * params beside the composed English sentence, so a refusal that names an
 * order code, a customer or their credit figures reaches an Arabic reader in
 * Arabic. One builder per parameterised member the API raises inside an HTTP
 * exception body — the raise sites throw THESE, and each `message` is
 * composed by the `ORDER_MESSAGES` twin so the two cannot drift (parity
 * pinned member by member in `apps/api/test/refusal-census.spec.ts`;
 * both-language renders in `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * Two parameterised siblings are deliberately NOT here: no API raise site
 * names `noDeliveryYet` or `noInvoiceYet` — the walls those §14 rows
 * describe are enforced at the delivery and invoice doors by those
 * families' own sentences (`DELIVERY_MESSAGES`, the ladder's fixed ones),
 * so a builder for either would be a raise shape with no raise site (the
 * `WAREHOUSE_MESSAGES.zoneHasBins` disposition). The credit pair's `to` and
 * `limit` travel PRE-FORMATTED by the raise site's own formatter —
 * '158,900.00' with its N-2 thousands commas, the `po.approvalRequired`
 * precedent — so dictionary and sentence cannot disagree on grouping. The
 * `overCreditLimitContinue` raise site spreads `overCreditLimit: true`
 * beside the structure (the ApiError body seam this route's 409 schema
 * names). No member carries a count: this family pluralises nothing.
 */
export const ORDER_REFUSALS = {
    cancelledNoDocs: (code) => ({
        message: ORDER_MESSAGES.cancelledNoDocs(code),
        key: 'orders.errCancelledNoDocs',
        params: { code },
    }),
    alreadyInvoiced: (code) => ({
        message: ORDER_MESSAGES.alreadyInvoiced(code),
        key: 'orders.errAlreadyInvoiced',
        params: { code },
    }),
    nothingNewToBill: (code) => ({
        message: ORDER_MESSAGES.nothingNewToBill(code),
        key: 'orders.errNothingNewToBill',
        params: { code },
    }),
    overCreditLimit: (name, to, limit) => ({
        message: ORDER_MESSAGES.overCreditLimit(name, to, limit),
        key: 'orders.errOverCreditLimit',
        params: { name, to, limit },
    }),
    overCreditLimitContinue: (name, to, limit) => ({
        message: ORDER_MESSAGES.overCreditLimitContinue(name, to, limit),
        key: 'orders.errOverCreditLimitContinue',
        params: { name, to, limit },
    }),
    /** Wave 4B: `code` is the BLOCKING document's printed number (DN-#### or INV-####). */
    reopenBlocked: (code) => ({
        message: ORDER_MESSAGES.reopenBlocked(code),
        key: 'orders.errReopenBlocked',
        params: { code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const OrderLineInputSchema = z
    .object({
    itemId: z.uuid().optional(),
    /**
     * Where the line ships from — the RESERVATION's address (stock_levels is
     * per item per warehouse). Only an item-linked line with a warehouse
     * reserves at confirm and is deliverable.
     */
    warehouseId: z.uuid().optional(),
    /** INV-L4 (SO-02): the unit the qty was typed in; absent = the item's stock unit. */
    unitId: z.uuid().optional(),
    description: z.string().trim().min(1).max(200),
    qty: QtyStringSchema,
    /** INV-L1: the free quantity given with the line; omitted = 0. */
    bonusQty: BonusQtyStringSchema.optional(),
    unitPrice: MoneyStringSchema,
    discountPct: PctStringSchema.optional(),
    /**
     * Wave 4B: a per-line delivery date for a STAGED order — "the chairs on
     * the 5th, the desks on the 12th". Absent = the header's expected date
     * governs. Informational: read by people, scheduled by nothing.
     */
    deliveryDate: IsoDateSchema.optional(),
})
    .strict();
export const OrderLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    itemId: z.uuid().nullable(),
    itemCode: z.string().nullable(),
    warehouseId: z.uuid().nullable(),
    unitId: z.uuid().nullable(),
    /** Wave 4C: the unit `qty` is in — the typed unit, else the item's stock unit; both null on a free-text line. */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    description: z.string(),
    qty: z.string(),
    bonusQty: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
    /** Σ delivered over POSTED delivery notes — the backorder math's left side. */
    deliveredQty: z.string(),
    /** Wave 4B: the line's own delivery date, or null when the header's governs. */
    deliveryDate: IsoDateSchema.nullable(),
})
    .strict();
/** Wave 4B — a delivery note raised on the order, for the form's linked chips. */
export const OrderDeliveryNoteLinkSchema = z
    .object({
    id: z.uuid(),
    /** DN-####. */
    code: z.string(),
    status: z.enum(['draft', 'posted', 'cancelled']),
})
    .strict();
export const OrderStatusSchema = z.enum([
    'draft',
    'confirmed',
    'partially_delivered',
    'fulfilled',
    'closed',
    'cancelled',
]);
/** §5d — the five order-to-cash stages, DERIVED from status + links. */
export const OrderStageSchema = z.enum(['quotation', 'sales_order', 'delivery', 'invoice', 'payment']);
export const OrderSchema = z
    .object({
    id: z.uuid(),
    /** SO-####. */
    code: z.string(),
    orderNo: z.number().int().positive(),
    status: OrderStatusSchema,
    /** GEN-59: why the order was cancelled — null until it is, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    /**
     * Derived (BR-SAL-02-02): draft = quotation; confirmed = sales_order;
     * invoice posted = invoice; invoice fully paid = payment; any delivered
     * percentage above zero = delivery.
     */
    stage: OrderStageSchema,
    orderDate: IsoDateSchema,
    expectedDate: IsoDateSchema.nullable(),
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /** INV-F1…F6 — the header the three sales documents share (SO-02). */
    ...SalesHeaderResponseFields,
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    /** Wave 4D — the sales commission (sales-invoices.ts). */
    ...CommissionResponseFields,
    /** §5c field 12 — "From QUO-####"; hidden when typed directly. */
    sourceQuotationId: z.uuid().nullable(),
    sourceQuotationCode: z.string().nullable(),
    /** Wave 4B: the source quotation's version at the time of reading ("QUO-0007 v2"). */
    sourceQuotationRevisionNo: z.number().int().positive().nullable(),
    /** BR-SAL-02-08 — the pill opens the invoice. */
    invoiceId: z.uuid().nullable(),
    invoiceCode: z.string().nullable(),
    /** Whether the linked invoice has been POSTED (drives the stage). */
    invoicePosted: z.boolean(),
    /** Wave 4B: every delivery note raised on this order, oldest first, cancelled ones included. */
    deliveryNotes: OrderDeliveryNoteLinkSchema.array(),
    /** Wave 4B: the customer's reference / PO number and the order's terms (see the migration). */
    reference: z.string().nullable(),
    terms: z.string().nullable(),
    /** BR-SAL-02-03 — "60% delivered · 0% invoiced". */
    deliveredPct: z.string(),
    invoicedPct: z.string(),
    lines: OrderLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    notes: z.string().nullable(),
    confirmedAt: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateOrderRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionInputFields,
    partnerId: z.uuid(),
    orderDate: IsoDateSchema,
    expectedDate: IsoDateSchema.optional(),
    ...SalesHeaderInputFields,
    /** Wave 4B: the customer's own reference / PO number, and the terms the order stands on. */
    reference: z.string().trim().max(50).optional(),
    terms: z.string().max(2000).optional(),
    /** SAL-02 §15 "Empty": a new order may have no lines; confirming may not. */
    lines: OrderLineInputSchema.array().optional(),
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateOrderRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionPatchFields,
    orderDate: IsoDateSchema.optional(),
    /** Editable after confirm too — a shipping estimate, not a commercial term. */
    expectedDate: IsoDateSchema.nullable().optional(),
    ...SalesHeaderPatchFields,
    /** Wave 4B: draft-side; null clears. */
    reference: z.string().trim().max(50).nullable().optional(),
    terms: z.string().max(2000).nullable().optional(),
    lines: OrderLineInputSchema.array().optional(),
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
const orderParams = z.object({ orderId: z.uuid() }).strict();
export const salesOrdersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/sales/orders',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: OrderStatusSchema.optional(),
            /** GEN-50: the printed order code, or the customer's name. */
            q: z.string().optional(),
            /**
             * BR-SAL-03's two statuses as ONE predicate — confirmed or partially
             * delivered. The delivery-note picker used to fetch every order and
             * narrow to these in JavaScript, which a page turns into a lie: 20
             * rows filtered down to 3 looks like a tenant with 3 open orders.
             */
            deliverable: z.enum(['true', 'false']).optional(),
            /** GEN-50: the order a delivery note is already holding. */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('orders', OrderSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Sales orders (sales.view), FILTERED IN SQL AND PAGED (NFR-42) and the delivery note’s "which order?" typeahead (GEN-50). The §5a tiles moved to `/stats` — a figure about the whole register cannot be folded out of one page.',
    },
    /**
     * SAL-02 §5a's three tiles, over the WHOLE register.
     *
     * They used to be a `.filter().length` and a `Money` fold over the array
     * the screen was rendering, which is PAGINATION.md §3 step 10's trap:
     * paging the list would have left three figures quietly describing
     * twenty-five rows. Same shape as `/api/items/stats` and
     * `/api/crm/leads/stats` — a figure about everything belongs in a route
     * about everything.
     */
    stats: {
        method: 'GET',
        path: '/api/sales/orders/stats',
        responses: {
            200: z
                .object({
                openOrders: z.number().int().min(0),
                /** Tax-included, as a 2dp decimal string. */
                backlog: MoneyStringSchema,
                awaitingInvoice: z.number().int().min(0),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'SAL-02 §5a tiles (sales.view): open orders, backlog value and awaiting-invoice count over the whole register, in SQL. BR-SAL-02-10 — cancelled, draft and closed are not open.',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the sales orders on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/sales/orders/:orderId/neighbours',
        pathParams: orderParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next sales order by number (sales.view).',
    },
    /** S4 — the "Go to no." box: one sales order by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/sales/orders/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One sales order by its printed number (sales.view).',
    },
    get: {
        method: 'GET',
        path: '/api/sales/orders/:orderId',
        pathParams: orderParams,
        responses: {
            200: OrderSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One order with its lines, links, derived stage and percentages (sales.view).',
    },
    create: {
        method: 'POST',
        path: '/api/sales/orders',
        body: CreateOrderRequestSchema,
        responses: {
            201: OrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft order (sales.create) — may start with no lines (SAL-02 §5b).',
    },
    update: {
        method: 'PATCH',
        path: '/api/sales/orders/:orderId',
        pathParams: orderParams,
        body: UpdateOrderRequestSchema,
        responses: {
            200: OrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (sales.edit). On a confirmed order only expected date and notes may change; cancelled is terminal.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/sales/orders/:orderId',
        pathParams: orderParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (sales.delete); a confirmed or cancelled order answers 409.',
    },
    confirm: {
        method: 'POST',
        path: '/api/sales/orders/:orderId/confirm',
        pathParams: orderParams,
        body: z
            .object({
            /**
             * BR-MD-01-10, Warn only — the "Continue?" answered yes, the
             * BR-MD-05-08 / acknowledgeShortage shape. Absent or false, a
             * confirm that would take the partner past their credit limit under
             * Warn enforcement answers 409 with the sentence plus "Continue?"
             * and `overCreditLimit: true`; resent with this true, it proceeds.
             * Meaningless under Block (always refused) and None (never asked).
             */
            continueOverLimit: z.boolean().optional(),
        })
            .strict()
            .optional(),
        responses: {
            200: OrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            /**
             * BR-MD-01-10's Warn refusal carries `overCreditLimit: true` so the
             * screen can tell "ask the question" from every other 409 without
             * matching a parameterised sentence (the ApiError body seam, GEN-93's
             * precedent for a refusal carrying more than its sentence). D46: the
             * flag rides BESIDE the structured half — `MessageSchema` carries the
             * optional `key`/`params` every converted refusal sends.
             */
            409: MessageSchema.extend({ overCreditLimit: z.boolean().optional() }),
        },
        summary: 'Confirm (sales.approve — 16.3 "for confirming"): Draft → Confirmed, the customer is committed. Needs lines and a total above zero. Reserves the quantity of each product line at its warehouse. Acts on the partner’s Credit enforcement when the order would pass their limit (BR-MD-01-10).',
    },
    cancel: {
        method: 'POST',
        path: '/api/sales/orders/:orderId/cancel',
        pathParams: orderParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, ORDER_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: OrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Cancel order (sales.approve; BR-SAL-02-07 — the button must exist). Terminal; the order stops counting toward the tiles (BR-SAL-02-10) and whatever reservation is left is released. GEN-59: reason required, stored on the order and written to the audit row.',
    },
    /**
     * Wave 4B — AMEND after confirmation: Confirmed → Draft, the way back
     * along SAL-02 §7's ladder for an order the customer changed before
     * anything moved. Reservations are released (the next confirm re-reserves
     * and re-freezes the rate); the reason rides the `sales_order.reopen`
     * audit row. Refused — with the blocking document's number — while any
     * delivery note or invoice that is not cancelled stands against the
     * order. A revision model (freeze v1, open v2) is deliberately NOT this:
     * an order has no customer-facing version to keep.
     */
    reopen: {
        method: 'POST',
        path: '/api/sales/orders/:orderId/reopen',
        pathParams: orderParams,
        body: z
            .object({
            reason: z.string().trim().min(1, ORDER_MESSAGES.reopenReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: OrderSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Amend (sales.approve): a confirmed or partially-delivered order with no delivery note and no invoice standing against it goes back to Draft; reservations are released and the typed reason is written to the audit row. Otherwise 409 naming the blocking document.',
    },
    createInvoice: {
        method: 'POST',
        path: '/api/sales/orders/:orderId/create-invoice',
        pathParams: orderParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: z.object({ order: OrderSchema, invoiceId: z.uuid() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Create invoice (sales.create): a DRAFT sales invoice carrying every line, linked back (BR-SAL-02-08), invoiced 100%. Confirmed orders only; cancelled answers with the BR-SAL-02-06 sentence.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=sales-orders.js.map